/**
 * Where a scan happened, when the browser will say without asking.
 *
 * Never prompts: a position is read only when geolocation permission is already granted. Anything
 * else (no API, prompt or denied state, timeout, error) resolves to null, so callers can always
 * await it before writing a record.
 */

/**
 * @typedef {Object} ScanPosition
 * @property {number} Latitude
 * @property {number} Longitude
 * @property {number} [Accuracy] - Metres.
 */

const DEFAULT_TIMEOUT_MS = 4000;
const DEFAULT_MAXIMUM_AGE_MS = 60000;

/**
 * @param {Object} [pOptions]
 * @param {number} [pOptions.TimeoutMS] - Give up after this long. Default 4 s.
 * @param {number} [pOptions.MaximumAgeMS] - Accept a cached fix this old. Default 60 s.
 * @param {any} [pOptions.Navigator] - For tests; defaults to the global navigator.
 * @return {Promise<ScanPosition|null>}
 */
async function getPositionIfPermitted(pOptions)
{
	const tmpOptions = pOptions || {};
	const tmpNavigator = tmpOptions.Navigator || ((typeof navigator !== 'undefined') ? navigator : null);
	if (!tmpNavigator || !tmpNavigator.geolocation || !tmpNavigator.permissions || typeof tmpNavigator.permissions.query !== 'function')
	{
		return null;
	}
	let tmpState = null;
	try
	{
		tmpState = (await tmpNavigator.permissions.query({ name: 'geolocation' })).state;
	}
	catch (pError)
	{
		return null;
	}
	if (tmpState !== 'granted')
	{
		return null;
	}
	const tmpTimeout = Number(tmpOptions.TimeoutMS) || DEFAULT_TIMEOUT_MS;
	return new Promise((fResolve) =>
	{
		let tmpDone = false;
		const fFinish = (pValue) =>
		{
			if (!tmpDone)
			{
				tmpDone = true;
				fResolve(pValue);
			}
		};
		setTimeout(() => fFinish(null), tmpTimeout + 250);
		try
		{
			tmpNavigator.geolocation.getCurrentPosition(
				(pPosition) =>
				{
					const tmpCoordinates = pPosition && pPosition.coords;
					if (!tmpCoordinates || !Number.isFinite(tmpCoordinates.latitude) || !Number.isFinite(tmpCoordinates.longitude))
					{
						fFinish(null);
						return;
					}
					fFinish({ Latitude: tmpCoordinates.latitude, Longitude: tmpCoordinates.longitude, Accuracy: tmpCoordinates.accuracy });
				},
				() => fFinish(null),
				{ enableHighAccuracy: false, timeout: tmpTimeout, maximumAge: (tmpOptions.MaximumAgeMS === undefined) ? DEFAULT_MAXIMUM_AGE_MS : tmpOptions.MaximumAgeMS });
		}
		catch (pError)
		{
			fFinish(null);
		}
	});
}

module.exports =
{
	getPositionIfPermitted,
};
